import classNames from "classnames";
import { FC, useEffect, useRef } from "react";

type Props = {
  label?: string;
  name: string;
  type: string;
  required?: boolean;
  error?: string;
  helperText?: string;
  value?: string | number;
  placeholder?: string;
  defaultValue?: string;
  size?: "xs" | "sm" | "md" | "lg";
  inputClassName?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  min?: string;
  max?: string;
  step?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  onClickOutside?: () => void;
};

const TextInput: FC<Props> = ({
  label,
  name,
  type,
  error,
  helperText,
  value,
  required,
  placeholder,
  defaultValue,
  size = "md",
  inputClassName,
  autoFocus,
  disabled,
  min,
  max,
  step,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  onPaste,
  onClickOutside,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node) && onClickOutside) {
        onClickOutside();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClickOutside]);

  return (
    <label className="form-control w-full">
      {label && (
        <div className="label">
          <span className="label-text">{label}</span>
        </div>
      )}
      <input
        ref={inputRef}
        type={type}
        placeholder={placeholder}
        className={classNames("input w-full", {
          "input-xs": size === "xs",
          "input-sm": size === "sm",
          "input-md": size === "md",
          "input-lg": size === "lg",
        }, inputClassName)}
        name={name}
        value={value}
        required={required}
        defaultValue={defaultValue}
        autoFocus={autoFocus}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
      />
      {(!!helperText || !!error) && (
        <div className="label gap-2">
          {helperText && <span className="label-text-alt">{helperText}</span>}
          {error && <span className="label-text-alt text-error">{error}</span>}
        </div>
      )}
    </label>
  );
};

export default TextInput;
